import React, { useState, useRef, useCallback } from 'react';
import api from '../services/api';
import {
  Send,
  Loader,
  Upload,
  X,
  FileText,
  User,
  Tag,
  Calendar,
  Camera,
  RefreshCw,
  CheckCircle,
  TrendingUp
} from 'lucide-react';
import './ManualNews.css';

function ManualNews() {
  // Form state
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [category, setCategory] = useState('');
  const [article, setArticle] = useState('');
  const [date, setDate] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  });

  // Photos state (3 slots)
  const [photos, setPhotos] = useState([null, null, null]);
  const [photoPreviews, setPhotoPreviews] = useState([null, null, null]);
  const fileInputRefs = [useRef(null), useRef(null), useRef(null)];

  // State
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [newsId, setNewsId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  // Trend state
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendResult, setTrendResult] = useState(null);

  // ── Photo handlers ──────────────────────────────────────────────────

  const handlePhotoSelect = (idx, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Файл слишком большой. Максимум 5MB');
      return;
    }
    const newPhotos = [...photos];
    newPhotos[idx] = file;
    setPhotos(newPhotos);

    const reader = new FileReader();
    reader.onload = (e) => {
      const newPreviews = [...photoPreviews];
      newPreviews[idx] = e.target.result;
      setPhotoPreviews(newPreviews);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (idx) => {
    const newPhotos = [...photos];
    const newPreviews = [...photoPreviews];
    newPhotos[idx] = null;
    newPreviews[idx] = null;
    setPhotos(newPhotos);
    setPhotoPreviews(newPreviews);
    if (fileInputRefs[idx].current) fileInputRefs[idx].current.value = '';
  };

  const handleDrop = useCallback((idx, e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handlePhotoSelect(idx, file);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handlePaste = useCallback((idx, e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) handlePhotoSelect(idx, file);
        break;
      }
    }
  }, []);

  // ── Upload photos helper ────────────────────────────────────────────

  const uploadPhotos = async () => {
    const filesToUpload = photos.filter(Boolean);
    if (filesToUpload.length === 0) return [];

    const formData = new FormData();
    filesToUpload.forEach(f => formData.append('photos', f));
    const res = await api.post('/api/admin/upload-photos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (!res.data.success) throw new Error(res.data.error || 'Upload failed');
    return res.data.photos || [];
  };

  // ── Publish ─────────────────────────────────────────────────────────

  const handlePublish = async () => {
    if (!title.trim()) { alert('Введите заголовок'); return; }
    if (!article.trim()) { alert('Введите статью'); return; }
    if (!window.confirm('Опубликовать новость в ленту?')) return;

    setPublishing(true);
    setStatusMessage('');

    try {
      // 1. Upload photos
      const photoUrls = await uploadPhotos();

      // 2. Create news without AI processing
      const createRes = await api.post('/api/admin/news', {
        sourceTitle: title.trim(),
        sourceArticle: article.trim(),
        sourceName: 'Manual',
        sourceLang: 'ru',
        author: author.trim() || undefined,
        category: category || undefined,
        publishedDate: date || undefined,
        photos: photoUrls.length > 0 ? photoUrls : undefined,
        options: {
          useAiImprove: false,
          useAiComic: false,
          useAiPhrases: false,
          useAiBubbles: false
        }
      });

      if (!createRes.data.success) {
        throw new Error(createRes.data.error || 'Ошибка создания');
      }

      const id = createRes.data.newsId;
      setNewsId(id);

      // 3. Publish immediately
      const pubRes = await api.post(`/api/admin/news/${id}/publish`);
      if (pubRes.data.success) {
        setPublished(true);
        setStatusMessage('Новость опубликована!');
      } else {
        throw new Error(pubRes.data.error || 'Ошибка публикации');
      }
    } catch (error) {
      console.error('Publish error:', error);
      setStatusMessage('Ошибка: ' + error.message);
    } finally {
      setPublishing(false);
    }
  };

  // ── Trend manual trigger ───────────────────────────────────────────

  const handleTrendSend = async () => {
    setTrendLoading(true);
    setTrendResult(null);
    try {
      const res = await api.post('/api/admin/trend/send');
      if (res.data.success) {
        const art = res.data.article;
        setTrendResult({
          success: true,
          message: art
            ? `Тренд запущен! Статья #${art.id}: "${(art.title || '').substring(0, 50)}" (score: ${art.score}, TG: ${art.sentTelegram ? '✓' : '✗'}, WA: ${art.sentWhatsApp ? '✓' : '✗'})`
            : res.data.message || 'Нет подходящей статьи'
        });
      } else {
        setTrendResult({ success: false, message: 'Ошибка: ' + (res.data.error || 'неизвестная') });
      }
    } catch (error) {
      setTrendResult({ success: false, message: 'Ошибка: ' + (error.response?.data?.error || error.message) });
    } finally {
      setTrendLoading(false);
    }
  };

  // ── Reset form ──────────────────────────────────────────────────────

  const resetForm = () => {
    setTitle('');
    setAuthor('');
    setCategory('');
    setArticle('');
    setNewsId(null);
    setPublished(false);
    setStatusMessage('');
    setPhotos([null, null, null]);
    setPhotoPreviews([null, null, null]);
    fileInputRefs.forEach(r => { if (r.current) r.current.value = ''; });
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    setDate(local.toISOString().slice(0, 16));
  };

  // ── Render ──────────────────────────────────────────────────────────

  const canPublish = title.trim() && article.trim() && !publishing && !published;

  return (
    <div className="manual-news-page">
      <div className="page-header-row">
        <div className="page-title-group">
          <FileText size={24} />
          <h1>Новость вручную</h1>
        </div>
        {published && (
          <button className="btn btn-ghost btn-sm" onClick={resetForm}>
            <RefreshCw size={16} />
            Новая новость
          </button>
        )}
      </div>

      <div className="mn-layout mn-layout-single">
        {/* ── Form ─────────────────────────────────────────── */}
        <div className="mn-form-panel mn-form-wide">
          <div className="card">
            <div className="card-header">
              <FileText size={18} />
              <h2>Данные новости</h2>
            </div>
            <div className="card-body">
              {/* Title */}
              <div className="input-group">
                <label className="input-label">
                  Заголовок <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="Заголовок новости"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  disabled={published}
                />
              </div>

              {/* Author */}
              <div className="input-group">
                <label className="input-label">
                  <User size={14} className="label-icon" /> Автор
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="Имя автора (необязательно)"
                  value={author}
                  onChange={e => setAuthor(e.target.value)}
                  disabled={published}
                />
              </div>

              {/* Category */}
              <div className="input-group">
                <label className="input-label">
                  <Tag size={14} className="label-icon" /> Категория
                </label>
                <select
                  className="input"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  disabled={published}
                >
                  <option value="">Без категории</option>
                  <option value="Премьера">Премьера</option>
                  <option value="Гастроли">Гастроли</option>
                  <option value="Назначения">Назначения</option>
                  <option value="Контракты">Контракты</option>
                  <option value="Рецензии">Рецензии</option>
                  <option value="Скандалы">Скандалы</option>
                  <option value="Инсайды">Инсайды</option>
                  <option value="Образование">Образование</option>
                  <option value="Интервью">Интервью</option>
                  <option value="Другое">Другое</option>
                </select>
              </div>

              {/* Article */}
              <div className="input-group">
                <label className="input-label">
                  Статья <span className="required">*</span>
                </label>
                <textarea
                  className="input mn-article-textarea"
                  placeholder="Текст статьи..."
                  value={article}
                  onChange={e => setArticle(e.target.value)}
                  rows={10}
                  disabled={published}
                />
                <div className="char-counter">{article.length} символов</div>
              </div>

              {/* Date */}
              <div className="input-group">
                <label className="input-label">
                  <Calendar size={14} className="label-icon" /> Дата
                </label>
                <input
                  type="datetime-local"
                  className="input"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  disabled={published}
                />
              </div>

              {/* Photos */}
              <div className="input-group">
                <label className="input-label">
                  <Camera size={14} className="label-icon" /> Фотографии
                </label>
                <div className="mn-photo-grid">
                  {['Фото 1', 'Фото 2', 'Фото 3'].map((label, idx) => (
                    <div
                      key={idx}
                      className={`mn-photo-slot ${photoPreviews[idx] ? 'has-photo' : ''}`}
                      onClick={() => !published && fileInputRefs[idx].current?.click()}
                      onDrop={e => !published && handleDrop(idx, e)}
                      onDragOver={handleDragOver}
                      onPaste={e => !published && handlePaste(idx, e)}
                      tabIndex={0}
                    >
                      {photoPreviews[idx] ? (
                        <>
                          <img src={photoPreviews[idx]} alt={label} className="mn-photo-preview" />
                          {!published && (
                            <button
                              className="mn-photo-remove"
                              onClick={e => { e.stopPropagation(); removePhoto(idx); }}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="mn-photo-placeholder">
                          <Upload size={20} />
                          <span>{label}</span>
                        </div>
                      )}
                      <input
                        ref={fileInputRefs[idx]}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={e => {
                          handlePhotoSelect(idx, e.target.files[0]);
                          e.target.value = '';
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="mn-form-actions">
                {statusMessage && (
                  <div className={`mn-status ${published ? 'mn-status-success' : statusMessage.startsWith('Ошибка') ? 'mn-status-error' : 'mn-status-info'}`}>
                    {published && <CheckCircle size={16} />}
                    {statusMessage}
                  </div>
                )}

                <button
                  className="btn btn-success mn-btn-full"
                  onClick={handlePublish}
                  disabled={!canPublish}
                >
                  {publishing ? (
                    <>
                      <Loader size={18} className="spin" />
                      Публикация...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Опубликовать
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Trend manual trigger ────────────────────────── */}
      <div className="mn-layout mn-layout-single" style={{ marginTop: 24 }}>
        <div className="mn-form-panel mn-form-wide">
          <div className="card">
            <div className="card-header">
              <TrendingUp size={18} />
              <h2>Трендовая статья дня</h2>
            </div>
            <div className="card-body">
              <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14 }}>
                Запустить отправку трендовой статьи в Telegram и WhatsApp группы вручную.
                Система выберет статью с наибольшим engagement и отправит в подключённые каналы.
              </p>

              {trendResult && (
                <div className={`mn-status ${trendResult.success ? 'mn-status-success' : 'mn-status-error'}`} style={{ marginBottom: 12 }}>
                  {trendResult.success && <CheckCircle size={16} />}
                  {trendResult.message}
                </div>
              )}

              <button
                className="btn btn-primary mn-btn-full"
                onClick={handleTrendSend}
                disabled={trendLoading}
                style={{ background: 'linear-gradient(135deg, #ff6b35, #f7c948)' }}
              >
                {trendLoading ? (
                  <>
                    <Loader size={18} className="spin" />
                    Запуск...
                  </>
                ) : (
                  <>
                    <TrendingUp size={18} />
                    Запустить тренд вручную
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManualNews;
